import "./Home.css";
import { Email, Phone } from "@material-ui/icons";
import GitHubIcon from "@material-ui/icons/GitHub";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import { useContext, useEffect, useRef } from "react";
import { logResumeClick, logSocialClick } from "../../supabaseTracker";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ThemeContext } from "../../contexts/theme";
import {
  formatDisplayName,
  getVisitorName,
  slugifyName,
} from "../../lib/visitorContext";

gsap.registerPlugin(ScrollTrigger);

const H = ({ children }) => <span className="impact-highlight">{children}</span>;

const resolveVisitorSlug = (visitorSlug) => {
  if (visitorSlug) return visitorSlug;
  const stored = getVisitorName();
  if (stored) return stored;
  try {
    const ref = new URLSearchParams(window.location.search).get("ref");
    return slugifyName(ref);
  } catch {
    return null;
  }
};

function Home({ visitorSlug }) {
  const rootRef = useRef(null);

  const founderSlug = resolveVisitorSlug(visitorSlug);
  const founderName = founderSlug ? formatDisplayName(founderSlug) : null;
  const isFounder = Boolean(founderName);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      if (isFounder) {
        tl.from(".hero-greeting-prefix", {
          opacity: 0,
          x: -12,
          duration: 0.45,
          ease: "power2.out",
        })
          .from(
            ".hero-founder-name",
            {
              opacity: 0,
              y: 32,
              scale: 0.88,
              duration: 0.8,
              ease: "back.out(1.8)",
            },
            "-=0.2"
          )
          .from(
            ".hero-wave",
            {
              opacity: 0,
              scale: 0,
              duration: 0.35,
              ease: "back.out(2)",
            },
            "-=0.35"
          )
          .from(
            ".hero-description",
            { y: 20, opacity: 0, duration: 0.6, ease: "power2.out" },
            "-=0.55"
          );

        gsap.to(".hero-wave", {
          rotation: 18,
          duration: 0.15,
          repeat: 10,
          yoyo: true,
          ease: "power1.inOut",
          transformOrigin: "70% 70%",
          delay: 0.85,
          onComplete: () => gsap.set(".hero-wave", { rotation: 0 }),
        });
      } else {
        tl.from(".hero-name", {
          y: 30,
          opacity: 0,
          duration: 0.6,
          ease: "power2.out",
        })
          .from(
            ".hero-subtitle",
            { y: 16, opacity: 0, duration: 0.5, ease: "power2.out" },
            "-=0.35"
          )
          .from(
            ".hero-description",
            { y: 20, opacity: 0, duration: 0.6, ease: "power2.out" },
            "-=0.3"
          );
      }

      tl.from(
        ".hero-social .link--icon",
          {
            y: 12,
            opacity: 0,
            duration: 0.4,
            stagger: 0.08,
            ease: "power2.out",
          },
          "-=0.4"
        )
        .from(
          ".hero-resume-btn",
          { y: 12, opacity: 1, duration: 0.4, ease: "power2.out" },
          "-=0.35"
        );

      gsap.to(".hero-video", {
        scale: 1.05,
        autoAlpha: 0.25,
        ease: "none",
        scrollTrigger: {
          trigger: rootRef.current,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    }, rootRef);

    return () => ctx.revert();
  }, [isFounder, founderName]);

  const themeName = useContext(ThemeContext);

  return (
    <section ref={rootRef} id="home" className="section home center">
      {themeName[0].themeName == "dark" && (
        <video
          className="hero-video"
          autoPlay
          loop
          muted
          playsInline
          onLoadedMetadata={(e) => {
            e.currentTarget.currentTime = 5;
            e.currentTarget.play();
          }}
        >
          <source src="/vidoes/hero.webm" type="video/webm" />
        </video>
      )}
      <div className="home-content">
        {isFounder ? (
          <h1 className="hero-name hero-name--founder">
            <span className="hero-greeting-prefix">Hi,</span>
            <span className="hero-founder-name">{founderName}</span>
            <span className="hero-wave" aria-hidden="true">
              👋
            </span>
          </h1>
        ) : (
          <>
            <h1 className="hero-name">
              <span className="about__name">Muhammad Aqib</span>
            </h1>
            <p className="hero-subtitle">Software Engineer · Full Stack · AI</p>
          </>
        )}

        <p className="hero-description">
          {isFounder ? (
            <>
              Thanks for looking into my profile.{' '}
              <span className="hero-context-lead">Quick context about me: </span>
              I build production web and mobile apps. I&apos;ve taken a trading
              journal to the App Store with <H>10K+ downloads</H>,{' '}
              <H>6K+ users</H>, and <H>20+ brokerages</H> at{' '}
              <H>99.8% uptime</H>. I&apos;ve built for{' '}
              <H>1,500+ franchise locations</H>, contributed at Expensify serving{' '}
              <H>15M+ users</H>, and ship AI in production with <H>B0Bot</H> and{' '}
              <H>RideNear MCP</H>. Feel free to scroll and explore my work.
            </>
          ) : (
            <>
              I ship production web and mobile apps with TypeScript, Node.js,
              and Next.js. I&apos;ve taken a trading journal from zero to{" "}
              <H>10K+ downloads</H> and <H>6K+ users</H> in months, contributed
              to <H>Expensify (15M+ users)</H>, and built <H>B0Bot</H>, a
              news-based AI agent through Google Summer of Code.
            </>
          )}
        </p>

        <section className="hero-social">
          <a
            href="https://github.com/AQIB-NAWAB"
            target="_blank"
            aria-label="github"
            className="link link--icon"
            onClick={() => logSocialClick('github', 'hero').catch(() => {})}
          >
            {" "}
            <GitHubIcon />{" "}
          </a>
          <a
            href="https://www.linkedin.com/in/m-aqib-nawab/"
            target="_blank"
            aria-label="linkedin"
            className="link link--icon"
            onClick={() => logSocialClick('linkedin', 'hero').catch(() => {})}
          >
            {" "}
            <LinkedInIcon />{" "}
          </a>
          <a
            href="mailto:aqibnawab1100@gmail.com"
            aria-label="email"
            className="link link--icon"
            onClick={() => logSocialClick('email', 'hero').catch(() => {})}
          >
            {" "}
            <Email />{" "}
          </a>
          <a
            href="tel:+923084556535"
            aria-label="phone"
            className="link link--icon"
            onClick={() => logSocialClick('phone', 'hero').catch(() => {})}
          >
            {" "}
            <Phone />{" "}
          </a>
        </section>

        <a
          href="https://drive.google.com/file/d/1DdajiDkbfIRn0OcqK3_rf8PmuhJFidxg/view?usp=sharing"
          target="_blank"
          rel="noreferrer noopener"
          className="hero-resume-btn"
          onClick={() => logResumeClick().catch(() => {})}
        >
          <span type="button" className="btn btn--hero">
            Download Resume
          </span>
        </a>
      </div>
    </section>
  );
}

export default Home;
